import { Outlet, Link, useLocation } from 'react-router-dom';
import { ImpersonationBanner } from '../admin/ImpersonationBanner';
import { ImpersonateUserPicker } from '../admin/ImpersonateUserPicker';
import { useIsImpersonating } from '@/hooks/useSession';
import { useSession } from '@/hooks/useSession';
import { useCanViewTailscaleMesh } from '@/hooks/useCanViewTailscaleMesh';
import { ProfileModal } from './ProfileModal';
import { ReportProblemButton } from '../ReportProblemButton';
import { useProfilePhoto } from '@/hooks/useProfilePhoto';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getInitials } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { usePageTitle } from '@/hooks/usePageTitle';
import { PageBreadcrumbProvider, usePageBreadcrumbExtension } from './PageBreadcrumbContext';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { PostgresRole } from '../ProtectedRoute';
import { ALL_ROLES } from '../ProtectedRoute';
import { isFullAdmin } from '@/types/session';
import { WMS_PERMISSIONS } from '@/features/wms/permissions';
import { EQUIPMENT_PERMISSIONS } from '@/features/operations-equipment/permissions';
import { ACCOUNTING_PERMISSIONS } from '@/features/accounting/permissions';
import { OS_PERMISSIONS } from '@/features/os/permissions';
import { TRANSMITTER_PERMISSIONS } from '@/features/operations-transmitters/permissions';
import { PLANETE_COURRIER_PAGE_ROLES } from '@/features/planete-courrier/permissions';
import {
  Activity,
  Building2,
  LayoutDashboard,
  Lightbulb,
  Radio,
  // ArrowRightLeft,
  Briefcase,
  LogOut,
  User,
  BarChart3,
  Bot,
  Wand2,
  ClipboardCheck,
  ChevronDown,
  ChevronRight,
  Users,
  AlertTriangle,
  // Cpu,
  Cog,
  Fingerprint,
  Wrench,
  // DollarSign,
  PanelLeftClose,
  PanelLeftOpen,
  Star,
  X,
  ListCheck,
  Calendar,
  CalendarClock,
  MapPin,
  Boxes,
  ClipboardList,
  HandHelping,
  Truck,
  PackageCheck,
  FileText,
  TrendingUp,
  TrendingDown,
  BookOpen,
  Globe,
  Newspaper,
  Megaphone,
  Smartphone,
  Target,
  Cloud,
  FolderTree,
  Tags,
  ShoppingCart,
  Battery,
  RotateCcw,
  Barcode,
  UserPlus,
  Code2,
  Network,
  Workflow,
  Search,
  Linkedin,
  Mountain,
  Compass,
  CheckSquare,
  Video,
  GitBranch,
  Calculator,
  Scale,
  Receipt,
  Copy,
  Package,
  Eye,
  Thermometer,
  ShieldCheck,
  GraduationCap,
  Presentation,
  Refrigerator,
} from 'lucide-react';
import { CUSTOMER_REQUEST_VIEWER_ROLES } from '@/features/customer-requests/constants';

const SIDEBAR_COLLAPSED_KEY = 'atek-sidebar-collapsed';
const SIDEBAR_BOOKMARKS_KEY = 'atek-sidebar-bookmarks';
const CALIBRATOR_ALLOWED_ROLES: PostgresRole[] = ['full_admin', 'director', 'operations_manager'];

/** Navigation item type with role-based access (recursive — children can have children) */
interface NavChild {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  /** Roles that can see this item. If empty/undefined, visible to all roles. */
  allowedRoles?: PostgresRole[];
  /** Team names required to see this item. Caller must be a member of at
   *  least one. Leadership team and full_admin/director always bypass.
   *  If empty/undefined, no team check is applied. */
  allowedTeams?: string[];
  /** Permission string required (from user.permission_strings). full_admin/director bypass. */
  requiredPermission?: string;
  /** Internal employee-only workflow. Backend enforces the same boundary. */
  employeesOnly?: boolean;
  /** ATEK employment only (employment_type employee/contractor; a PG role is NOT
   *  enough) — mirrors useCanViewTailscaleMesh. Backend enforces the same boundary. */
  internalOnly?: boolean;
  /** Nested sub-group children (renders as collapsible accordion) */
  children?: NavChild[];
  /** Optional badge label (e.g. "Alpha", "Beta") */
  badge?: string;
  /** When true, `href` is an external URL: renders a plain <a target="_blank">
   *  instead of a React-Router <Link>, and never gets active-route highlighting. */
  external?: boolean;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  /** Roles that can see this section. If empty/undefined, visible to all roles. */
  allowedRoles?: PostgresRole[];
  /** Team names required to see this section. Caller must be a member of at
   *  least one. Leadership team and full_admin/director always bypass.
   *  If empty/undefined, no team check is applied. */
  allowedTeams?: string[];
  /** Permission string required (from user.permission_strings). full_admin/director bypass. */
  requiredPermission?: string;
  children?: NavChild[];
  /** Optional badge label (e.g. "Alpha", "Beta") */
  badge?: string;
  /** ATEK employment only (employment_type employee/contractor; a PG role is NOT
   *  enough) — mirrors useCanViewTailscaleMesh. Backend enforces the same boundary. */
  internalOnly?: boolean;
}

/**
 * Navigation items for the admin sidebar with role-based access control
 *
 * Access Matrix:
 * - full_admin: ALL routes
 * - director: ALL routes (same as full_admin)
 * - sales_manager: accounts, intelligence, crm, operations, kpis, changelog
 * - operations_manager: accounts, intelligence, wms, operations, kpis, changelog
 * - quality_manager: accounts, intelligence, issues (under OS), operations, kpis, changelog
 */
// Green-tinted receipt icon for the QBO Invoices menu entry.
// Wraps lucide's Receipt so it always renders in text-green-600, merging any
// incoming className from the sidebar's own `icon` class.
function GreenReceipt({ className }: { className?: string }) {
  return <Receipt className={className} style={{ color: '#16a34a' }} />;
}
function badgeClassName(label: string): string {
  const base = 'nav-badge px-1.5 py-0.5 text-[10px] font-semibold rounded leading-none';
  if (label === 'β') return `${base} bg-blue-500/20 text-blue-400`;
  if (label === 'γ') return `${base} bg-purple-500/20 text-purple-400`;
  return `${base} bg-amber-500/20 text-amber-400`;
}
function GreenTrendingUp({ className }: { className?: string }) {
  return <TrendingUp className={className} style={{ color: '#16a34a' }} />;
}
function GreenTrendingDown({ className }: { className?: string }) {
  return <TrendingDown className={className} style={{ color: '#16a34a' }} />;
}

const navigation: NavItem[] = [
  {
    name: 'Customer Accounts',
    href: '/accounts',
    icon: Building2,
    description: 'Manage accounts',
    allowedRoles: ALL_ROLES,
    children: [
      {
        name: 'AM Dashboard',
        href: '/am-dashboard',
        icon: LayoutDashboard,
        badge: 'β',
        description: 'Account Manager portfolio overview',
        allowedRoles: ['full_admin', 'director', 'sales_manager', 'csm'] as PostgresRole[],
      },
      {
        name: 'Customer Requests',
        href: '/customer-requests',
        icon: Lightbulb,
        badge: 'β',
        description: 'What customers ask us to build, ranked by votes',
        allowedRoles: CUSTOMER_REQUEST_VIEWER_ROLES,
      },
      {
        name: 'Accounts',
        href: '/accounts',
        icon: Briefcase,
        badge: 'β',
        description: 'Manage accounts',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'Account Health',
        href: '/intelligence/customers',
        icon: Users,
        badge: 'α',
        description: 'Customer health',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'Subscriptions',
        href: '/accounts/subscriptions',
        icon: FileText,
        badge: 'α',
        description: 'Subscription management',
        allowedRoles: ALL_ROLES,
        requiredPermission: 'subscriptions.read',
      },
    ],
  },
  {
    name: 'Operations',
    href: '/operations',
    icon: Cog,
    description: 'Operations management',
    allowedRoles: ALL_ROLES,
    children: [
      {
        name: 'Dashboard',
        href: '/operations/dashboard',
        icon: LayoutDashboard,
        description: 'Operations dashboard',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'Equipment library',
        href: '/operations/equipment',
        icon: Refrigerator,
        badge: 'β', // beta
        description: 'Models, install guides, manuals',
        allowedRoles: ALL_ROLES,
        requiredPermission: EQUIPMENT_PERMISSIONS.read,
      },
      {
        name: 'Kanban',
        href: '/operations/dashboard/kanban',
        icon: Briefcase,
        description: 'Projects and tasks in Kanban view',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'Tasks',
        href: '/operations/tasks',
        icon: ListCheck,
        description: 'Tasks management',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'Pipeline',
        href: '/operations/quotes-pipeline',
        icon: FileText,
        description: 'Track quotes through the sales pipeline',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'Calendar',
        href: '/operations/calendar',
        icon: Calendar,
        description: 'Calendar management',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'Jobs',
        href: '/operations/installations',
        icon: Wrench,
        description: 'Technician installation jobs',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'RMA',
        href: '/operations/rma',
        icon: RotateCcw,
        badge: 'β', // beta
        description: 'Return Merchandise Authorization',
        allowedRoles: ['full_admin', 'operations_manager', 'director'],
      },
      {
        name: 'Shipping',
        href: '/tools/shipping/planete-courrier',
        icon: Truck,
        description: 'Courier & shipping management',
        allowedRoles: ALL_ROLES,
        children: [
          {
            name: 'Planète Courrier',
            href: '/tools/shipping/planete-courrier',
            icon: Truck,
            description: 'Planète Courrier shipments & invoices',
            allowedRoles: PLANETE_COURRIER_PAGE_ROLES,
          },
          {
            name: 'Canada Post',
            href: '/tools/shipping/canada-post',
            icon: Package,
            description: 'Canada Post tracking lookup',
            allowedRoles: ALL_ROLES,
          },
          {
            name: 'DHL',
            href: '/tools/shipping/dhl',
            icon: Truck,
            description: 'DHL Express shipping & tracking',
            allowedRoles: ALL_ROLES,
          },
          {
            name: 'CARM',
            href: '/erp/carm',
            icon: Globe,
            description: 'CBSA customs declarations, tariffs & duties',
            allowedRoles: ALL_ROLES,
          },
        ],
      },
      {
        name: 'Customers',
        href: '/operations/customers',
        icon: Users,
        description: 'Customer operations',
        allowedRoles: ALL_ROLES,
        children: [
          // @TODO: add back when account migration features are built out. This is a placeholder for the future "Account Migration" section that will allow moving assets between accounts.
          // {
          //   name: 'Account Migration',
          //   href: '/operations/account-migration',
          //   icon: ArrowRightLeft,
          //   description: 'Move assets between accounts',
          //   allowedRoles: ALL_ROLES,
          // },
          {
            name: 'Inactive Sensors',
            href: '/operations/inactive-sensors',
            icon: AlertTriangle,
            description: 'Sensors needing attention',
            allowedRoles: ALL_ROLES,
          },
          {
            name: 'Customer Onboarding',
            href: '/operations/customer-onboarding',
            icon: UserPlus,
            description: 'Customer onboarding',
            allowedRoles: ALL_ROLES,
          },
        ],
      },
      {
        name: 'Calibration',
        href: '/operations/calibration',
        icon: Fingerprint,
        description: 'Calibration management',
        allowedRoles: ALL_ROLES,
        children: [
          {
            name: 'Due Calibrations',
            href: '/operations/calibration/certificates',
            icon: Fingerprint,
            badge: 'β', // beta
            description: 'Calibration certificates overview',
            allowedRoles: ALL_ROLES,
            requiredPermission: 'calibrationrecords.read',
          },
          {
            name: 'Certificate Validation',
            href: '/operations/calibration/validation',
            icon: ClipboardCheck,
            badge: 'β', // beta
            description: 'Certificate review, F-770 register and statistics',
            allowedRoles: ALL_ROLES,
            requiredPermission: 'calibrationrecords.read',
          },
          {
            name: 'Planning Cockpit',
            href: '/operations/calibration/planning',
            icon: BarChart3,
            badge: 'β',
            description: 'Calibration planning cockpit',
            allowedRoles: ALL_ROLES,
            requiredPermission: 'calibrationrecords.read',
          },
          {
            name: 'Calibrators',
            href: '/operations/calibrators',
            icon: Fingerprint,
            badge: 'β', // beta
            description: 'Calibration equipment management',
            allowedRoles: CALIBRATOR_ALLOWED_ROLES,
          },
          {
            name: 'Procedures',
            href: '/operations/calibration-procedures',
            icon: ClipboardList,
            badge: 'β', // beta
            description: 'Calibration procedures management',
            allowedRoles: CALIBRATOR_ALLOWED_ROLES,
          },
        ],
      },
      {
        name: 'Validation',
        href: '/operations/validation/installation-qualifications',
        icon: ClipboardCheck,
        description: 'Validation records and certificates',
        allowedRoles: ALL_ROLES,
        children: [
          {
            name: 'Installation Qualification',
            href: '/operations/validation/installation-qualifications',
            icon: ClipboardCheck,
            badge: 'β', // beta
            description: 'Installation qualification reports',
            allowedRoles: ALL_ROLES,
          },
        ],
      },
      {
        name: 'SIM Cards',
        href: '/operations/sim-cards',
        icon: Smartphone,
        description: 'Jasper and Telnyx IoT SIM inventory',
        allowedRoles: ALL_ROLES,
      },
      // {
      //   name: 'Milesight Calibration',
      //   href: '/operations/lora-calibration',
      //   icon: Radio,
      //   description: 'Remote LoRaWAN sensor calibration',
      //   allowedRoles: ['full_admin', 'director'],
      // },
      // {
      //   name: 'Settings',
      //   href: '/operations/settings',
      //   icon: Cog,
      //   description: 'Operations settings and permissions',
      //   allowedRoles: ['full_admin', 'director'],
      // },
    ],
  },
  {
    name: 'CRM',
    href: '/crm/pipeline',
    icon: TrendingUp,
    description: 'Customer relationship management',
    // Parent must include every role that any visible child uses, otherwise
    // the parent (and therefore all its children) are filtered out of the nav.
    allowedRoles: ['full_admin', 'director', 'sales_manager', 'operations_manager'] as PostgresRole[],
    children: [
      {
        name: 'Companies',
        href: '/crm/companies',
        icon: Building2,
        description: 'Manage companies and prospects',
        allowedRoles: ['full_admin', 'director', 'sales_manager', 'operations_manager'] as PostgresRole[],
      },
      {
        name: 'Contacts',
        href: '/crm/contacts',
        icon: Users,
        description: 'Manage contacts',
        allowedRoles: ['full_admin', 'director', 'sales_manager', 'operations_manager'] as PostgresRole[],
      },
      {
        name: 'Opportunities',
        href: '/crm/pipeline',
        icon: TrendingUp,
        description: 'Manage opportunities through the sales process',
        // Same roles as the /crm/pipeline and /crm/opportunities/:id routes in App.tsx.
        allowedRoles: ['full_admin', 'director', 'sales_manager'] as PostgresRole[],
      },
      {
        name: 'Tenders',
        href: '/crm/signals/tender-hunter',
        icon: Radio,
        description: 'Crawl procurement sources for RFQs',
        allowedRoles: ['full_admin'] as PostgresRole[],
      },
      {
        name: 'Estimates',
        href: '/crm/estimates',
        icon: Calculator,
        description: 'Monitoring estimates, approvals and proposal decks',
        allowedRoles: ['full_admin', 'director', 'sales_manager'] as PostgresRole[],
      },
      {
        name: 'Proposal templates',
        href: '/crm/settings/proposal-templates',
        icon: Presentation,
        description: 'Google Slides templates for proposal decks',
        allowedRoles: ['full_admin', 'director'] as PostgresRole[],
      },
    ],
  },
  {
    name: 'Marketing',
    href: '/marketing',
    icon: Megaphone,
    description: 'Marketing campaigns and outreach',
    allowedRoles: ALL_ROLES,
    badge: 'α',
    children: [
      {
        name: 'Publication',
        href: '/marketing/publication',
        icon: Calendar,
        description: 'Publication schedule (calendar, kanban, table)',
        allowedRoles: ALL_ROLES,
        requiredPermission: 'marketing.publication.access',
        badge: 'α',
      },
      {
        name: 'Brand',
        href: '/marketing/brand',
        icon: Star,
        description: 'Brand guide — colors, logos, personas, official images',
        allowedRoles: ALL_ROLES,
        requiredPermission: 'marketing.brand.access',
      },
      {
        name: 'LinkedIn',
        href: '/crm/linkedin',
        icon: Linkedin,
        description: 'Browse LinkedIn and manage outreach',
        allowedRoles: ALL_ROLES,
        requiredPermission: 'marketing.linkedin.access',
        badge: 'α',
      },
      {
        name: 'Apollo',
        href: '/crm/apollo',
        icon: Search,
        description: 'Search Apollo.io for people and companies',
        allowedRoles: ALL_ROLES,
        requiredPermission: 'marketing.apollo.access',
        badge: 'α',
      },
      {
        name: 'Web Crawler',
        href: '/crm/signals/crawlers',
        icon: Globe,
        description: 'AI-powered web crawling with VLM',
        allowedRoles: ALL_ROLES,
        requiredPermission: 'marketing.webcrawler.access',
        badge: 'α',
      },
      {
        name: 'News',
        href: '/crm/signals/news-hunter',
        icon: Radio,
        description: 'Monitor news for procurement signals',
        allowedRoles: ALL_ROLES,
        requiredPermission: 'marketing.news.access',
        badge: 'α',
      },
      {
        name: 'Maintenance Events',
        href: '/tools/maintenance-events',
        icon: CalendarClock,
        description: 'Schedule customer maintenance notices',
        badge: 'β', // beta
        allowedRoles: ALL_ROLES,
        requiredPermission: 'marketing.maintenance_events.access',
      },
    ],
  },
  {
    name: 'Intelligence',
    href: '/intelligence',
    icon: BarChart3,
    description: 'Analytics dashboards',
    allowedRoles: ALL_ROLES,
    children: [
      {
        name: 'Battery',
        href: '/intelligence/battery',
        icon: Battery,
        description: 'Battery voltage monitoring',
        allowedRoles: ALL_ROLES,
      },
      // {
      //   name: 'Forecasts',
      //   href: '/intelligence/forecasts',
      //   icon: TrendingUp,
      //   description: 'Sensor forecasting & anomaly detection',
      //   allowedRoles: ['full_admin', 'director', 'operations_manager', 'sales_manager'] as const,
      // },
      {
        name: 'Cloud Spend',
        href: '/settings/cloud-spend',
        icon: Cloud,
        description: 'GCP cloud spend monitoring',
        allowedRoles: ['full_admin', 'director'],
      },
    ],
  },
  {
    name: 'WMS',
    href: '/wms',
    icon: Boxes,
    description: 'Warehouse Management System',
    allowedRoles: ALL_ROLES,
    children: [
      {
        name: 'Dashboard',
        href: '/wms',
        icon: LayoutDashboard,
        description: 'WMS Overview',
        allowedRoles: ALL_ROLES,
        requiredPermission: WMS_PERMISSIONS.dashboard.access,
      },
      {
        name: 'Locations',
        href: '/wms/locations',
        icon: MapPin,
        description: 'Warehouse locations',
        allowedRoles: ALL_ROLES,
        requiredPermission: WMS_PERMISSIONS.locations.read,
      },
      {
        name: 'Inventory',
        href: '/wms/inventory',
        icon: Boxes,
        description: 'Inventory management',
        allowedRoles: ALL_ROLES,
        requiredPermission: WMS_PERMISSIONS.inventory.read,
      },
      // {
      //   name: 'Serialized Items',
      //   href: '/wms/serials',
      //   icon: Hash,
      //   description: 'Serial number tracking',
      //   allowedRoles: ALL_ROLES,
      //   requiredPermission: WMS_PERMISSIONS.serials.read,
      // },
      // {
      //   name: 'Work Orders',
      //   href: '/wms/work-orders',
      //   icon: ClipboardList,
      //   description: 'Manufacturing work orders',
      //   allowedRoles: ALL_ROLES,
      //   requiredPermission: WMS_PERMISSIONS.workOrders.read,
      // },
      {
        name: 'Picking',
        href: '/wms/picking',
        icon: HandHelping,
        description: 'Pick lists',
        allowedRoles: ALL_ROLES,
        requiredPermission: WMS_PERMISSIONS.picking.read,
      },
      // {
      //   name: 'Shipping',
      //   href: '/wms/shipping',
      //   icon: Truck,
      //   description: 'Packing slips',
      //   allowedRoles: ALL_ROLES,
      //   requiredPermission: WMS_PERMISSIONS.shipping.read,
      // },
      {
        name: 'Procurement',
        href: '/procurement',
        icon: PackageCheck,
        description: 'Procurement orders',
        allowedRoles: ALL_ROLES,
        requiredPermission: WMS_PERMISSIONS.procurement.read,
      },
      {
        name: 'Suppliers',
        href: '/wms/suppliers',
        icon: Building2,
        description: 'Supplier management',
        allowedRoles: ALL_ROLES,
        requiredPermission: WMS_PERMISSIONS.suppliers.read,
      },
      {
        name: 'SKUs',
        href: '/skus',
        icon: Barcode,
        description: 'Product catalog',
        allowedRoles: ALL_ROLES,
        requiredPermission: WMS_PERMISSIONS.skus.read,
      },
      // {
      //   name: 'Settings',
      //   href: '/wms/settings',
      //   icon: Cog,
      //   description: 'WMS settings',
      //   allowedRoles: ['full_admin', 'director'],
      // },
    ],
  },
  {
    name: 'ERP',
    href: '/erp',
    icon: BookOpen,
    description: 'ERP management',
    allowedRoles: ALL_ROLES,
    children: [
      {
        name: 'All Users',
        href: '/erp/users',
        icon: Users,
        badge: 'α',
        description: 'Platform user management',
        allowedRoles: ALL_ROLES,
      },
      // {
      //   name: 'Roles',
      //   href: '/erp/roles',
      //   icon: Fingerprint,
      //   description: 'Manage RBAC roles and permissions',
      //   allowedRoles: ALL_ROLES,
      // },
      {
        name: 'All Sensors',
        href: '/operations/sensors',
        icon: Radio,
        description: 'Full sensor fleet management',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'All Transmitters',
        href: '/operations/transmitters',
        icon: Radio,
        description: 'IoT device management',
        allowedRoles: ALL_ROLES,
        requiredPermission: TRANSMITTER_PERMISSIONS.read,
      },
      {
        name: 'All Gateways',
        href: '/operations/gateways',
        icon: Radio,
        description: 'IoT gateway management',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'VPN Mesh',
        href: '/operations/mesh',
        icon: Network,
        description: 'All machines on the Headscale tailnet',
        allowedRoles: ['full_admin', 'director'],
        internalOnly: true,
      },
      {
        name: 'All Streams',
        href: '/operations/streams',
        icon: Radio,
        description: 'Stream calibration management',
        allowedRoles: ['full_admin', 'director'],
      },
      {
        name: 'All Quotes',
        href: '/erp/quotes',
        icon: FileText,
        description: 'Quote management',
        allowedRoles: ALL_ROLES,
        requiredPermission: 'quotes.read',
      },
      {
        name: 'All Orders',
        href: '/erp/orders',
        icon: ShoppingCart,
        description: 'Order management',
        allowedRoles: ALL_ROLES,
        requiredPermission: 'orders.read',
      },
      {
        name: 'All Invoices',
        href: '/erp/invoices',
        icon: FileText,
        description: 'Invoice management',
        allowedRoles: ALL_ROLES,
        requiredPermission: 'invoices.read',
      },
      {
        name: 'All Sites',
        href: '/erp/sites',
        icon: MapPin,
        description: 'Site management',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'All Locations',
        href: '/erp/locations',
        icon: MapPin,
        description: 'Location management',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'Shippings',
        href: '/erp/shippings',
        icon: Truck,
        description: 'Shipping management',
        allowedRoles: ALL_ROLES,
      },
      {
        name: 'Settings',
        href: '/erp/settings',
        icon: Cog,
        description: 'ERP settings',
        allowedRoles: ['full_admin', 'director'],
      },
    ],
  },
  {
    name: 'Company Pulse',
    href: '/company-pulse',
    icon: Activity,
    description: 'Shared company goals and progress',
    allowedRoles: ['employee', 'full_admin', 'director', 'sales_manager', 'operations_manager', 'quality_manager', 'csm'],
  },
  {
    name: 'OS',
    href: '/os',
    icon: Compass,
    description: 'EOS Operating System',
    allowedRoles: ALL_ROLES,
    children: [
      { name: 'KPIs', href: '/kpis', icon: BarChart3, description: 'Key performance indicators', allowedRoles: ALL_ROLES, requiredPermission: OS_PERMISSIONS.kpis.read },
      { name: 'Rocks', href: '/rocks', icon: Mountain, description: 'Quarterly priorities', allowedRoles: ALL_ROLES, requiredPermission: OS_PERMISSIONS.rocks.read, badge: 'β' },
      { name: 'Headlines', href: '/headlines', icon: Megaphone, description: 'Team announcements', allowedRoles: ALL_ROLES, requiredPermission: OS_PERMISSIONS.headlines.read },
      { name: 'Issues', href: '/qms?type=ticket', icon: ClipboardCheck, description: 'Issues tracking', allowedRoles: ALL_ROLES, requiredPermission: 'qmstickets.read' },
      { name: 'To-Dos', href: '/todos', icon: CheckSquare, description: 'Action items', allowedRoles: ALL_ROLES, requiredPermission: OS_PERMISSIONS.todos.read, badge: 'α' },
      { name: 'Meetings', href: '/os/meetings', icon: Video, description: 'L10 meetings', allowedRoles: ALL_ROLES, requiredPermission: OS_PERMISSIONS.meetings.read, badge: 'α' },
      { name: 'V/TO', href: '/vto', icon: Target, description: 'Vision/Traction Organizer', allowedRoles: ALL_ROLES, requiredPermission: OS_PERMISSIONS.vto.read, badge: 'α' },
    ],
  },
  {
    name: 'eQMS',
    href: '/eqms/training',
    icon: ClipboardList,
    description: 'Electronic Quality Management System',
    allowedRoles: ALL_ROLES,
    children: [
      { name: 'Command Center', href: '/eqms', icon: LayoutDashboard, description: 'Controlled documents and quality evidence', allowedRoles: ['full_admin', 'director', 'quality_manager'] as PostgresRole[] },
      { name: 'Forms & Procedure Executions', href: '/eqms/forms', icon: ClipboardList, description: 'Controlled forms, collaborative executions, and completed records', employeesOnly: true },
      { name: 'Training', href: '/eqms/training', icon: GraduationCap, description: 'Assigned SOP training and qualification', allowedRoles: ALL_ROLES },
      { name: 'Training Compliance', href: '/eqms/training?tab=compliance', icon: Users, description: 'Employee qualification and training matrix', allowedRoles: ['full_admin', 'director', 'quality_manager'] as PostgresRole[] },
      { name: 'Validation & Readiness', href: '/eqms/validation', icon: PackageCheck, description: 'Requirements, evidence, reviews, and inspection packages', allowedRoles: ['full_admin', 'director', 'quality_manager'] as PostgresRole[] },
      { name: 'Quality Events', href: '/qms?type=ticket', icon: ClipboardCheck, description: 'Issues tracking', allowedRoles: ALL_ROLES, requiredPermission: 'qmstickets.read' },
      { name: 'CAPA', href: '/qms?type=capa', icon: ShieldCheck, description: 'Corrective and preventive actions', allowedRoles: ALL_ROLES, requiredPermission: 'qmstickets.read' },
    ],
  },
  {
    name: 'HRIS',
    href: '/hris',
    icon: UserPlus,
    description: 'Human Resources Information System',
    allowedRoles: ['full_admin'] as PostgresRole[],
    badge: 'α',
    children: [
      { name: 'Employees', href: '/employees', icon: Users, description: 'Employee directory', allowedRoles: ['full_admin'] as PostgresRole[], badge: 'α' },
      { name: 'Teams', href: '/team', icon: Users, description: 'Teams & memberships', allowedRoles: ['full_admin'] as PostgresRole[], badge: 'α' },
      { name: 'Roles', href: '/roles-doc', icon: Fingerprint, description: 'Role definitions & capabilities', allowedRoles: ['full_admin', 'director'] as PostgresRole[], badge: 'α' },
      { name: 'Seats', href: '/seats', icon: GitBranch, description: 'Accountability seats', allowedRoles: ['full_admin', 'director'] as PostgresRole[], badge: 'α' },
      { name: 'Org Chart', href: '/org-chart', icon: GitBranch, description: 'Reporting hierarchy', allowedRoles: ['full_admin', 'director'] as PostgresRole[], badge: 'α' },
      { name: 'Processes', href: '/processes', icon: Workflow, description: 'Process maps, procedures & decisions', allowedRoles: ['full_admin', 'director'] as PostgresRole[], badge: 'α' },
      { name: 'Jobs', href: '/jobs', icon: Briefcase, description: 'Postes ouverts & feed Indeed', allowedRoles: ['full_admin'] as PostgresRole[], badge: 'α' },
      { name: 'Recrutement', href: '/recruitment', icon: ClipboardList, description: 'Kanban des candidatures', allowedRoles: ['full_admin'] as PostgresRole[], badge: 'α' },
    ],
  },
  {
    name: 'Accounting',
    href: '/acc/pnl',
    icon: Calculator,
    description: 'Financial management (QuickBooks & bank reconciliation)',
    allowedRoles: ALL_ROLES,
    badge: 'α',
    children: [
      {
        name: 'Dashboard',
        href: '/acc/dashboard',
        icon: LayoutDashboard,
        description: 'Bookkeeper health — KPIs, review queue, activity feed',
        allowedRoles: ALL_ROLES,
        requiredPermission: ACCOUNTING_PERMISSIONS.pnl.read,
        badge: 'α',
      },
      {
        name: 'P&L',
        href: '/acc/pnl',
        icon: GreenTrendingUp,
        description: 'QuickBooks Profit & Loss dashboard',
        allowedRoles: ALL_ROLES,
        requiredPermission: ACCOUNTING_PERMISSIONS.pnl.read,
        badge: 'α',
      },
      {
        name: 'Balance Sheet',
        href: '/acc/balance-sheet',
        icon: Scale,
        description: 'QuickBooks Balance Sheet',
        allowedRoles: ALL_ROLES,
        requiredPermission: ACCOUNTING_PERMISSIONS.pnl.read,
        badge: 'α',
      },
      {
        name: 'Invoices',
        href: '/acc/invoices',
        icon: GreenReceipt,
        description: 'Invoices — ATEK & QuickBooks',
        allowedRoles: ALL_ROLES,
        requiredPermission: ACCOUNTING_PERMISSIONS.invoices.read,
        badge: 'α',
      },
      {
        name: 'Reconciliations',
        href: '/acc/statements',
        icon: FileText,
        description: 'Bank reconciliation',
        allowedRoles: ALL_ROLES,
        requiredPermission: ACCOUNTING_PERMISSIONS.reconciliations.read,
        badge: 'α',
      },
      {
        name: 'Receivable',
        href: '/acc/ar',
        icon: GreenTrendingUp,
        description: 'Accounts receivable aging',
        allowedRoles: ALL_ROLES,
        requiredPermission: ACCOUNTING_PERMISSIONS.receivable.read,
        badge: 'α',
      },
      {
        name: 'Payable',
        href: '/acc/ap',
        icon: GreenTrendingDown,
        description: 'Accounts payable aging',
        allowedRoles: ALL_ROLES,
        requiredPermission: ACCOUNTING_PERMISSIONS.payable.read,
        badge: 'α',
      },
      {
        name: 'Bills',
        href: '/acc/bills',
        icon: GreenTrendingDown,
        description: 'Vendor bills synced from QuickBooks',
        allowedRoles: ALL_ROLES,
        requiredPermission: ACCOUNTING_PERMISSIONS.bills.read,
        badge: 'α',
      },
      {
        name: 'Suppliers',
        href: '/acc/suppliers',
        icon: GreenTrendingDown,
        description: 'All supplier accounts (hardware, software, service, admin, other)',
        allowedRoles: ALL_ROLES,
        requiredPermission: ACCOUNTING_PERMISSIONS.suppliers.read,
        badge: 'α',
      },
    ],
  },
  {
    name: 'Tools',
    href: '/tools',
    icon: Code2,
    description: 'Developer tools',
    allowedRoles: [] as PostgresRole[],
    children: [
      {
        name: 'AI',
        href: '/ai',
        icon: Wand2,
        description: 'AI agents',
        allowedRoles: ['full_admin'] as PostgresRole[],
        children: [
          {
            name: 'Agents',
            href: '/ai/agents',
            icon: Bot,
            description: 'View registered Mastra agents',
            allowedRoles: ['full_admin'] as PostgresRole[],
          },
          {
            name: 'Workflows',
            href: '/ai/workflows',
            icon: Workflow,
            description: 'View workflow diagrams',
            allowedRoles: ['full_admin'] as PostgresRole[],
            badge: 'α',
          },
          {
            name: 'Mastra',
            href: '/ai/mastra',
            icon: Network,
            description: 'Mastra',
            allowedRoles: ['full_admin'] as PostgresRole[],
            badge: 'α',
          },
          {
            name: 'Workflow Scheduler',
            href: '/tools/scheduled-jobs',
            icon: Calendar,
            description: 'Manage recurring Mastra workflow triggers',
            badge: 'α',
            allowedRoles: ['full_admin'] as PostgresRole[],
          },
        ],
      },
      {
        name: 'Network Topology',
        href: '/operations/network-topology',
        icon: Network,
        description: 'Zigbee mesh network visualization (WIP — not functional)',
        allowedRoles: ['full_admin'] as PostgresRole[],
      },
      {
        name: 'Packet Interpreter',
        href: '/tools/packet-interpreter',
        icon: Code2,
        description: 'ATEK UDP packet decoder (WIP — not functional)',
        allowedRoles: ['full_admin'] as PostgresRole[],
      },
      {
        name: 'Transmitter Raw Events',
        href: '/tools/transmitter-raw-events',
        icon: Search,
        badge: 'β', // beta
        description: 'Debug raw transmitter ingress data',
        allowedRoles: [] as PostgresRole[],
      },
      {
        name: 'PostgreSQL Profiler',
        href: '/tools/postgres-profiler',
        icon: BarChart3,
        badge: 'β',
        description: 'Inspect slow PostgreSQL queries',
        allowedRoles: ['full_admin'] as PostgresRole[],
      },
      // {
      //   name: 'Dragino LHT65',
      //   href: '/tools/packet-interpreter/dragino-lht65',
      //   icon: Code2,
      //   description: 'Dragino LHT65 LoRaWAN packet decoder',
      //   allowedRoles: ALL_ROLES,
      // },
      {
        name: 'API Keys',
        href: '/tools/api-keys',
        icon: Fingerprint,
        description: 'Manage API keys for external integrations',
        badge: 'α',
        allowedRoles: ['full_admin'] as PostgresRole[],
      },
      {
        name: 'Calibration Tools',
        href: 'https://calibration.atek.io',
        external: true,
        icon: Thermometer,
        description: 'ATEK calibration platform (probes, certificates)',
        allowedRoles: ['full_admin'] as PostgresRole[],
      },
      {
        name: 'Documents',
        href: '/documents',
        icon: FileText,
        description: 'Document management system',
        badge: 'α',
        allowedRoles: ['full_admin'] as PostgresRole[],
        children: [
          {
            name: 'Documents',
            href: '/documents',
            icon: FileText,
            description: 'Document browser',
            allowedRoles: ['full_admin'] as PostgresRole[],
          },
          {
            name: 'Classification',
            href: '/documents/classification',
            icon: FolderTree,
            description: 'Manage classification views',
            allowedRoles: ['full_admin'] as PostgresRole[],
          },
          {
            name: 'Tags',
            href: '/documents/tags',
            icon: Tags,
            description: 'Manage tags and tag groups',
            allowedRoles: ['full_admin'] as PostgresRole[],
          },
          {
            name: 'Duplicates',
            href: '/documents/duplicates',
            icon: Copy,
            description: 'Find and resolve duplicate documents',
            allowedRoles: ['full_admin'] as PostgresRole[],
          },
          {
            name: 'Settings',
            href: '/documents/settings',
            icon: Cog,
            description: 'Document access and permissions',
            allowedRoles: ['full_admin'] as PostgresRole[],
          },
        ],
      },
    ],
  },
  {
    name: 'Resources',
    href: '/resources',
    icon: BookOpen,
    description: 'Resources and documentation',
    allowedRoles: ALL_ROLES,
    children: [
      {
        name: 'ATEK Brain',
        href: '/wiki-assistant',
        icon: BookOpen,
        description: 'ATEK internal knowledge chat',
        allowedRoles: ALL_ROLES,
        badge: 'β',
      },
      {
        name: 'Changelog',
        href: '/changelog',
        icon: Newspaper,
        description: "What's new in ATEK",
        allowedRoles: ALL_ROLES,
      },
    ],
  },
];

/**
 * Check if user has access to a navigation item based on their roles, team
 * membership, and permission strings. full_admin/director and Leadership team
 * members always pass team checks (canonical L10 escape hatch — mirrors the
 * isLeadership() bypass used in headlines/kpis/rocks routes).
 */
function hasAccess(
  userRoles: string[] | undefined,
  userPermissions: string[] | undefined,
  userTeamNames: string[] | undefined,
  allowedRoles: PostgresRole[] | undefined,
  allowedTeams: string[] | undefined,
  requiredPermission?: string,
): boolean {
  // If no roles specified, allow all (role check)
  const roleOk = !allowedRoles || allowedRoles.length === 0 || (userRoles?.some(role => allowedRoles.includes(role as PostgresRole)) ?? false);
  if (!roleOk) return false;

  // Team membership check. full_admin/director and Leadership team members
  // bypass. Otherwise the caller must be on at least one of allowedTeams.
  if (allowedTeams && allowedTeams.length > 0) {
    const isSuperAdmin = userRoles?.includes('full_admin') || userRoles?.includes('director');
    const isLeadership = userTeamNames?.includes('Leadership') ?? false;
    if (!isSuperAdmin && !isLeadership) {
      const teamOk = userTeamNames?.some(t => allowedTeams.includes(t)) ?? false;
      if (!teamOk) return false;
    }
  }

  // If no permission required, allow
  if (!requiredPermission) return true;
  // full_admin/director bypass permission checks, matching web_api_server deco.auth
  if (userRoles?.includes('full_admin') || userRoles?.includes('director')) return true;
  // Check permission_strings
  return userPermissions?.includes(requiredPermission) ?? false;
}

/**
 * Format role name for display (e.g., 'full_admin' -> 'Full Admin')
 */
function formatRoleName(role: string): string {
  return role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}


/** Recursive nav children renderer — supports collapsible sub-groups at any depth */
function NavChildrenList({
  children,
  depth,
  location,
  expandedMenus,
  toggleMenu,
  bookmarks,
  toggleBookmark,
}: {
  children: NavChild[];
  depth: number;
  location: { pathname: string };
  expandedMenus: Set<string>;
  toggleMenu: (name: string) => void;
  bookmarks: Set<string>;
  toggleBookmark: (href: string) => void;
}) {
  /** Check if any descendant leaf is active (recursive) */
  const hasActiveDescendant = (items: NavChild[]): boolean => {
    return items.some((item) => {
      if (item.children && item.children.length > 0) {
        return hasActiveDescendant(item.children);
      }
      return location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    });
  };

  return (
    <ul className={`nav-submenu ${depth >= 2 ? 'nav-submenu-nested' : ''}`}>
      {children.map((child) => {
        const hasChildren = child.children && child.children.length > 0;

        if (hasChildren) {
          // Sub-group: collapsible accordion
          const isExpanded = expandedMenus.has(child.name);
          const isActive = hasActiveDescendant(child.children!);
          return (
            <li key={`${child.name}-${child.href}`}>
              <button
                onClick={() => toggleMenu(child.name)}
                className={`nav-item nav-item-subgroup ${isActive ? 'active' : ''}`}
                style={{ paddingLeft: `${18 + depth * 30}px` }}
              >
                <child.icon className="icon" />
                <span>{child.name}</span>
                {isExpanded ? (
                  <ChevronDown className="chevron-icon" />
                ) : (
                  <ChevronRight className="chevron-icon" />
                )}
              </button>
              {isExpanded && (
                <NavChildrenList
                  children={child.children!}
                  depth={depth + 1}
                  location={location}
                  expandedMenus={expandedMenus}
                  toggleMenu={toggleMenu}
                  bookmarks={bookmarks}
                  toggleBookmark={toggleBookmark}
                />
              )}
            </li>
          );
        }

        // Leaf item: link with bookmark.
        // External items never participate in active-route highlighting.
        const isChildActive =
          !child.external &&
          (location.pathname === child.href ||
            location.pathname.startsWith(child.href + '/'));
        const navItemClassName = `nav-item nav-item-child ${isChildActive ? 'active' : ''}`;
        const navItemStyle = depth >= 2 ? { paddingLeft: `${18 + depth * 30}px` } : undefined;
        const navItemContent = (
          <>
            <child.icon className="icon" />
            <span>{child.name}</span>
            {child.badge && (
              <span className={badgeClassName(child.badge)}>
                {child.badge}
              </span>
            )}
            <button
              className={`bookmark-star ${bookmarks.has(child.href) ? 'bookmarked' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleBookmark(child.href);
              }}
              aria-label={bookmarks.has(child.href) ? `Remove ${child.name} bookmark` : `Bookmark ${child.name}`}
            >
              <Star className="h-3.5 w-3.5" fill={bookmarks.has(child.href) ? 'currentColor' : 'none'} />
            </button>
          </>
        );
        return (
          <li key={`${child.name}-${child.href}`}>
            {child.external ? (
              <a
                href={child.href}
                target="_blank"
                rel="noopener noreferrer"
                className={navItemClassName}
                style={navItemStyle}
              >
                {navItemContent}
              </a>
            ) : (
              <Link
                to={child.href}
                className={navItemClassName}
                style={navItemStyle}
              >
                {navItemContent}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Admin layout component with header and sidebar navigation
 * Styled to match the legacy ATEK blue theme
 */
export function AdminLayout() {
  const { user } = useSession();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [impersonatePickerOpen, setImpersonatePickerOpen] = useState(false);
  const isImpersonating = useIsImpersonating();
  const isSuperAdmin = isFullAdmin(user);
  const [profilePhotoLocal] = useProfilePhoto(user?._id);
  // Source-of-truth fallback chain: localStorage (this browser) → PG GCS
  // URL (cross-device). Surfaces the photo on every machine the user
  // signs in from, even ones that never uploaded a photo themselves.
  const profilePhoto = profilePhotoLocal || user?.profile_photo_url || null;
  usePageTitle();
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return stored === 'true';
  });

  const [bookmarks, setBookmarks] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_BOOKMARKS_KEY);
      return stored ? new Set(JSON.parse(stored) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleBookmark = useCallback((href: string) => {
    setBookmarks(prev => {
      const next = new Set(prev);
      if (next.has(href)) {
        next.delete(href);
      } else {
        next.add(href);
      }
      localStorage.setItem(SIDEBAR_BOOKMARKS_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Filter navigation based on user's PostgreSQL roles, team membership, and permission_strings
  const userRoles = user?.postgres_roles;
  const userPermissions = user?.permission_strings;
  const userTeamNames = useMemo(
    () => (user?.teams || []).map((t) => t?.name).filter((n): n is string => typeof n === 'string'),
    [user?.teams],
  );

  // Strict ATEK-employment gate for internalOnly nav items (Tailscale mesh).
  // Single source of truth: the same hook the mesh pages use.
  const canViewTailscaleMesh = useCanViewTailscaleMesh();

  // Recursively filter nav children by role + team + permission access
  const filterChildren = useCallback((children: NavChild[]): NavChild[] => {
    return children
      .filter(child => !child.employeesOnly || (user?.employment_type === 'employee' && (!user.employment_end_date || new Date(user.employment_end_date).getTime() >= Date.now())))
      .filter(child => !child.internalOnly || canViewTailscaleMesh)
      .filter(child => hasAccess(userRoles, userPermissions, userTeamNames, child.allowedRoles, child.allowedTeams, child.requiredPermission))
      .map(child => ({
        ...child,
        children: child.children ? filterChildren(child.children) : undefined,
      }))
      // Remove sub-group parents that have no visible children
      .filter(child => !child.children || child.children.length > 0);
  }, [userRoles, userPermissions, userTeamNames, user?.employment_type, user?.employment_end_date, canViewTailscaleMesh]);
  const filteredNavigation = useMemo(() => {
    return navigation
      .filter(item => !item.internalOnly || canViewTailscaleMesh)
      .filter(item => hasAccess(userRoles, userPermissions, userTeamNames, item.allowedRoles, item.allowedTeams, item.requiredPermission))
      .map(item => ({
        ...item,
        children: item.children ? filterChildren(item.children) : undefined,
      }))
      // Remove parent items that have no visible children
      .filter(item => !item.children || item.children.length > 0);
  }, [userRoles, userPermissions, userTeamNames, filterChildren]);

  // Flatten all visible nav items for bookmark lookup (recursive)
  const navItemsByHref = useMemo(() => {
    const map = new Map<string, { name: string; href: string; icon: React.ComponentType<{ className?: string }> }>();
    const flattenChildren = (children: NavChild[]) => {
      for (const child of children) {
        if (!child.children || child.children.length === 0) {
          map.set(child.href, { name: child.name, href: child.href, icon: child.icon });
        }
        if (child.children) {
          flattenChildren(child.children);
        }
      }
    };
    for (const item of filteredNavigation) {
      if (!item.children || item.children.length === 0) {
        map.set(item.href, { name: item.name, href: item.href, icon: item.icon });
      }
      if (item.children) {
        flattenChildren(item.children);
      }
    }
    return map;
  }, [filteredNavigation]);

  // Only show bookmarks that still exist in the filtered navigation (respects role access)
  const visibleBookmarks = useMemo(() => {
    return [...bookmarks].filter(href => navItemsByHref.has(href));
  }, [bookmarks, navItemsByHref]);

  // Derive the current page's nav item for header title display
  const currentNavItem = useMemo(() => {
    if (navItemsByHref.has(location.pathname)) {
      return navItemsByHref.get(location.pathname)!;
    }
    // Prefix match — longest matching prefix wins (for nested routes)
    let best: { name: string; href: string; icon: React.ComponentType<{ className?: string }> } | undefined;
    let bestLen = 0;
    for (const [href, item] of navItemsByHref) {
      if (location.pathname.startsWith(href + '/') && href.length > bestLen) {
        best = item;
        bestLen = href.length;
      }
    }
    return best;
  }, [navItemsByHref, location.pathname]);

  // Persist sidebar collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
  }, []);

  // Auto-expand parent menu (and sub-groups) if a child route is active
  React.useEffect(() => {
    const checkChildren = (children: NavChild[], parentsToExpand: string[]) => {
      for (const child of children) {
        const isActive = location.pathname === child.href || location.pathname.startsWith(child.href + '/');
        if (isActive) {
          setExpandedMenus((prev) => {
            const next = new Set(prev);
            parentsToExpand.forEach(name => next.add(name));
            return next;
          });
        }
        if (child.children) {
          checkChildren(child.children, [...parentsToExpand, child.name]);
        }
      }
    };
    filteredNavigation.forEach((item) => {
      if (item.children) {
        checkChildren(item.children, [item.name]);
      }
    });
  }, [location.pathname, filteredNavigation]);

  const toggleMenu = (menuName: string) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuName)) {
        newSet.delete(menuName);
      } else {
        newSet.add(menuName);
      }
      return newSet;
    });
  };

  // Sidebar search
  const [sidebarSearch, setSidebarSearch] = useState('');

  /** Filter navigation recursively — keep items whose name matches, preserving parent hierarchy */
  const searchFilteredNavigation = useMemo(() => {
    const q = sidebarSearch.trim().toLowerCase();
    if (!q) return filteredNavigation;

    const filterChildren = (children: NavChild[]): NavChild[] => {
      const result: NavChild[] = [];
      for (const child of children) {
        const nameMatch = child.name.toLowerCase().includes(q);
        const filtered = child.children ? filterChildren(child.children) : undefined;
        const hasMatchingChildren = filtered && filtered.length > 0;
        if (nameMatch || hasMatchingChildren) {
          result.push({ ...child, children: filtered });
        }
      }
      return result;
    };

    const result: NavItem[] = [];
    for (const item of filteredNavigation) {
      const nameMatch = item.name.toLowerCase().includes(q);
      const filtered = item.children ? filterChildren(item.children) : undefined;
      const hasMatchingChildren = filtered && filtered.length > 0;
      if (nameMatch || hasMatchingChildren) {
        result.push({ ...item, children: filtered });
      }
    }
    return result;
  }, [filteredNavigation, sidebarSearch]);

  /** When searching, expand all sections so matches are visible */
  const searchExpandedMenus = useMemo(() => {
    if (!sidebarSearch.trim()) return expandedMenus;
    const all = new Set<string>();
    const collect = (children: NavChild[]) => {
      for (const child of children) {
        if (child.children && child.children.length > 0) {
          all.add(child.name);
          collect(child.children);
        }
      }
    };
    for (const item of searchFilteredNavigation) {
      all.add(item.name);
      if (item.children) collect(item.children);
    }
    return all;
  }, [sidebarSearch, searchFilteredNavigation, expandedMenus]);

  const activeExpandedMenus = sidebarSearch.trim() ? searchExpandedMenus : expandedMenus;

  // Hover flyout (only when sidebar is collapsed)
  const [flyout, setFlyout] = useState<{ item: NavItem; top: number } | null>(null);
  const flyoutCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelFlyoutClose = useCallback(() => {
    if (flyoutCloseTimer.current) {
      clearTimeout(flyoutCloseTimer.current);
      flyoutCloseTimer.current = null;
    }
  }, []);

  const scheduleFlyoutClose = useCallback(() => {
    cancelFlyoutClose();
    flyoutCloseTimer.current = setTimeout(() => setFlyout(null), 120);
  }, [cancelFlyoutClose]);

  const openFlyout = useCallback((item: NavItem, e: React.MouseEvent<HTMLElement>) => {
    cancelFlyoutClose();
    const rect = e.currentTarget.getBoundingClientRect();
    setFlyout({ item, top: rect.top });
  }, [cancelFlyoutClose]);

  // Close flyout when sidebar expands or route changes
  useEffect(() => {
    setFlyout(null);
  }, [isSidebarCollapsed, location.pathname]);

  useEffect(() => () => cancelFlyoutClose(), [cancelFlyoutClose]);

  return (
    <PageBreadcrumbProvider>
      <div className="admin-layout">
      <ImpersonationBanner />
      {/* Header - matching legacy blue theme */}
      <header className="admin-header print:hidden">
        <div className="flex items-center gap-4">
          {/* Logo */}
          <a href="https://v2.atek.io" className="flex items-center gap-2 text-white hover:text-white">
            <img
              src="/atek/branding/Logo-Long_white.svg"
              alt="ATEK"
              className="h-7"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </a>

          {/* Admin badge */}
          <span className="px-2 py-0.5 text-[10px] font-semibold bg-atek-coral text-white rounded uppercase tracking-wide">
            Admin
          </span>

          {/* Current page title */}
          <HeaderPageTitle currentNavItem={currentNavItem} />
        </div>

        <div className="flex items-center gap-5">
          <Link
            to="/wiki-assistant"
            className={`flex items-center gap-1.5 rounded px-2 py-1 text-sm transition-colors ${
              location.pathname === '/wiki-assistant'
                ? 'bg-white/10 text-white'
                : 'text-atek-text-muted hover:text-white hover:bg-white/5'
            }`}
            title="Open ATEK Brain"
          >
            <BookOpen className="h-4 w-4" />
            <span>ATEK Brain</span>
          </Link>

          {/* Legacy app link */}
          <a
            href="https://v1.atek.io"
            className="flex items-center gap-1.5 text-sm"
            target="_blank"
            rel="noopener noreferrer"
          >
            <LayoutDashboard className="h-4 w-4" />
            <span>Legacy V1</span>
          </a>

          {/* Report a problem — always visible (issue #1268) */}
          <ReportProblemButton />

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 text-sm text-atek-text-muted hover:text-white transition-colors focus:outline-none"
                title="Profile menu"
              >
                <Avatar className="h-7 w-7 ring-1 ring-white/20">
                  {profilePhoto && (
                    <AvatarImage
                      src={profilePhoto}
                      alt={`${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim()}
                    />
                  )}
                  <AvatarFallback className="bg-white/10 text-white text-[11px] font-medium">
                    {getInitials(`${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim() || user?.email || 'U')}
                  </AvatarFallback>
                </Avatar>
                <span>
                  {user?.firstname} {user?.lastname}
                </span>
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-900">
                    {user?.firstname} {user?.lastname}
                  </span>
                  {user?.email && (
                    <span className="text-xs text-gray-500 font-normal truncate">{user.email}</span>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {/* Account */}
              <DropdownMenuItem onSelect={() => setProfileOpen(true)} className="cursor-pointer">
                <User className="h-4 w-4 mr-2" />
                My Profile
              </DropdownMenuItem>

              {/* Super-admin: View as user */}
              {isSuperAdmin && !isImpersonating && (
                <DropdownMenuItem
                  onSelect={() => setImpersonatePickerOpen(true)}
                  className="cursor-pointer"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View as user…
                </DropdownMenuItem>
              )}

              {/* Session */}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={async () => {
                  try {
                    await fetch('/api/v4/auth/logout', {
                      method: 'POST',
                      credentials: 'include',
                    });
                  } catch {
                    // Even if the request fails, fall through to /login —
                    // the cookie may already be invalid and useSession will
                    // bounce on next render.
                  }
                  window.location.href = '/login';
                }}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="admin-container">
        {/* Sidebar - matching legacy blue theme */}
        <aside className={`admin-sidebar flex flex-col print:hidden ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          {/* Toggle button */}
          <button
            onClick={toggleSidebar}
            className="sidebar-toggle"
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isSidebarCollapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </button>

          {/* Search filter */}
          {!isSidebarCollapsed && (
            <div className="px-3 pt-2 pb-1">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-atek-text-muted pointer-events-none" />
                <input
                  type="text"
                  value={sidebarSearch}
                  onChange={(e) => setSidebarSearch(e.target.value)}
                  placeholder="Search menu..."
                  className="w-full h-7 pl-7 pr-7 text-xs bg-white/10 border border-white/10 rounded text-white placeholder:text-atek-text-muted focus:outline-none focus:border-white/30 focus:bg-white/15"
                />
                {sidebarSearch && (
                  <button
                    onClick={() => setSidebarSearch('')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-atek-text-muted hover:text-white"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}

          <nav className="flex-1">
            {/* Bookmarks section — hidden during search */}
            {visibleBookmarks.length > 0 && !sidebarSearch.trim() && (
              <div className="bookmarks-section">
                {!isSidebarCollapsed && <div className="nav-section-title">Bookmarks</div>}
                <ul>
                  {visibleBookmarks.map(href => {
                    const item = navItemsByHref.get(href);
                    if (!item) return null;
                    const Icon = item.icon;
                    const isActive = location.pathname === href || location.pathname.startsWith(href + '/');
                    return (
                      <li key={href}>
                        <Link
                          to={href}
                          className={`nav-item bookmark-item ${isActive ? 'active' : ''}`}
                          title={isSidebarCollapsed ? item.name : undefined}
                        >
                          <Icon className="icon" />
                          {!isSidebarCollapsed && (
                            <>
                              <span>{item.name}</span>
                              <button
                                className="bookmark-remove"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleBookmark(href);
                                }}
                                aria-label={`Remove ${item.name} bookmark`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Main navigation - filtered by user roles and search */}
            {!isSidebarCollapsed && <div className="nav-section-title">Admin Modules</div>}
            {sidebarSearch.trim() && searchFilteredNavigation.length === 0 && (
              <div className="px-4 py-3 text-xs text-atek-text-muted">No results</div>
            )}
            <ul>
              {searchFilteredNavigation.map((item) => {
                const isExpanded = activeExpandedMenus.has(item.name);
                const children = item.children ?? [];
                const hasChildren = children.length > 0;
                const isParentActive = hasChildren
                  ? children.some((child) =>
                      location.pathname === child.href || location.pathname.startsWith(child.href + '/')
                    )
                  : location.pathname.startsWith(item.href);

                return (
                  <li key={item.href}>
                    {hasChildren ? (
                      <>
                        {/* Parent menu item with sub-menus */}
                        <button
                          onClick={() => isSidebarCollapsed ? null : toggleMenu(item.name)}
                          onMouseEnter={isSidebarCollapsed ? (e) => openFlyout(item, e) : undefined}
                          onMouseLeave={isSidebarCollapsed ? scheduleFlyoutClose : undefined}
                          className={`nav-item nav-item-parent ${isParentActive ? 'active' : ''}`}
                          title={isSidebarCollapsed ? undefined : item.name}
                        >
                          <item.icon className="icon" />
                          {!isSidebarCollapsed && (
                            <>
                              <span>{item.name}</span>
                              {item.badge && (
                                <span className={badgeClassName(item.badge)}>
                                  {item.badge}
                                </span>
                              )}
                              {isExpanded ? (
                                <ChevronDown className="chevron-icon" />
                              ) : (
                                <ChevronRight className="chevron-icon" />
                              )}
                            </>
                          )}
                        </button>

                        {/* Sub-menu items - hidden when collapsed */}
                        {isExpanded && !isSidebarCollapsed && (
                          <NavChildrenList
                            children={children}
                            depth={1}
                            location={location}
                            expandedMenus={activeExpandedMenus}
                            toggleMenu={toggleMenu}
                            bookmarks={bookmarks}
                            toggleBookmark={toggleBookmark}
                          />
                        )}
                      </>
                    ) : (
                      /* Regular menu item without sub-menus */
                      <Link
                        to={item.href}
                        className={`nav-item ${isParentActive ? 'active' : ''}`}
                        title={isSidebarCollapsed ? item.name : undefined}
                      >
                        <item.icon className="icon" />
                        {!isSidebarCollapsed && (
                          <>
                            <span>{item.name}</span>
                            <button
                              className={`bookmark-star ${bookmarks.has(item.href) ? 'bookmarked' : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleBookmark(item.href);
                              }}
                              aria-label={bookmarks.has(item.href) ? `Remove ${item.name} bookmark` : `Bookmark ${item.name}`}
                            >
                              <Star className="h-3.5 w-3.5" fill={bookmarks.has(item.href) ? 'currentColor' : 'none'} />
                            </button>
                          </>
                        )}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>

          </nav>

          {/* Hover flyout — only when sidebar is collapsed */}
          {isSidebarCollapsed && flyout && flyout.item.children && flyout.item.children.length > 0 && (
            <div
              className="sidebar-flyout"
              style={{ top: flyout.top }}
              onMouseEnter={cancelFlyoutClose}
              onMouseLeave={scheduleFlyoutClose}
              role="menu"
            >
              <div className="sidebar-flyout-title">
                <flyout.item.icon className="sidebar-flyout-icon" />
                <span>{flyout.item.name}</span>
              </div>
              <NavChildrenList
                children={flyout.item.children}
                depth={0}
                location={location}
                expandedMenus={activeExpandedMenus}
                toggleMenu={toggleMenu}
                bookmarks={bookmarks}
                toggleBookmark={toggleBookmark}
              />
            </div>
          )}

          {/* User info at bottom - hidden when collapsed */}
          {!isSidebarCollapsed && (
            <div className="user-info">
              <div className="user-email">{user?.email}</div>
              <div className="user-role">
                {user?.postgres_roles && user.postgres_roles.length > 0
                  ? user.postgres_roles.map(formatRoleName).join(', ')
                  : 'No roles assigned'}
              </div>
            </div>
          )}
        </aside>

        {/* Main content */}
        <main className="admin-content">
          <Outlet />
        </main>
      </div>

      {/* Self-service profile editor */}
      <ProfileModal open={profileOpen} onOpenChange={setProfileOpen} />
      <ImpersonateUserPicker open={impersonatePickerOpen} onOpenChange={setImpersonatePickerOpen} />
      </div>
    </PageBreadcrumbProvider>
  );
}

function HeaderPageTitle({
  currentNavItem,
}: {
  currentNavItem?: { name: string; href: string; icon: React.ComponentType<{ className?: string }> };
}) {
  const extension = usePageBreadcrumbExtension();
  if (!currentNavItem) return null;
  return (
    <span
      className="flex items-center gap-2 text-white uppercase tracking-widest text-sm font-light"
      style={{ fontFamily: "'Work Sans', sans-serif" }}
    >
      {/* Section title is a link back to its list page — e.g. clicking
          "ACCOUNTS" while on /accounts/<id> returns to the accounts table. */}
      <Link
        to={currentNavItem.href}
        className="flex items-center gap-2 text-white hover:text-white/80 transition-colors"
      >
        <currentNavItem.icon className="h-4 w-4" />
        {currentNavItem.name}
      </Link>
      {extension && (
        <>
          <span className="opacity-50">/</span>
          <span className="normal-case tracking-normal">{extension}</span>
        </>
      )}
    </span>
  );
}
